import React, { useState } from 'react';
import { Button, Icon } from '@/components/atoms';
import { Input } from '@kisanlink/ui-commons';
import {
  createPurchaseOrderSchema,
  type CreatePurchaseOrder,
  type CreatePurchaseOrderItem,
} from '@/schemas/purchaseOrders';
import {
  useCreatePurchaseOrderMutation,
  useCollaboratorsList,
  useWarehousesList,
  useCollaboratorVariants,
} from '@/hooks/useErpApi';
import { useDispatch } from 'react-redux';
import { addPurchaseOrder } from '@/features/purchaseOrders';

interface PurchaseOrderFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

interface CollaboratorVariant {
  id: string;
  product_name: string;
  product_sku: string;
  brand_name?: string;
  variant_name: string;
}

export const PurchaseOrderForm: React.FC<PurchaseOrderFormProps> = ({ onSuccess, onCancel }) => {
  const dispatch = useDispatch();
  const { mutate: createPurchaseOrder, isLoading } = useCreatePurchaseOrderMutation();
  const { data: collaboratorsData } = useCollaboratorsList();
  const { data: warehousesData } = useWarehousesList();

  // Helper to extract array from various API response shapes
  function extractArray<T>(data: unknown): T[] {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    const d = data as any;
    if (d?.data && Array.isArray(d.data)) return d.data;
    if (d?.data?.data && Array.isArray(d.data.data)) return d.data.data;
    // Check for named keys
    for (const key of Object.keys(d)) {
      if (Array.isArray(d[key]) && key !== 'pagination') return d[key];
    }
    return [];
  }

  const collaborators = React.useMemo(() => extractArray<any>(collaboratorsData), [collaboratorsData]);
  const warehouses = React.useMemo(() => extractArray<any>(warehousesData), [warehousesData]);

  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState({
    collaborator_id: '',
    warehouse_id: '',
    expected_delivery_date: '',
    is_inter_state: null as boolean | null, // null = auto-detect, true = IGST, false = CGST+SGST
  });
  
  // Fetch variants for the selected collaborator using the dedicated API
  // This API endpoint: GET /api/v1/collaborators/{collaborator_id}/variants
  // The hook automatically fetches when collaborator_id changes (enabled: !!collaboratorId)
  const {
    data: collaboratorVariantsResponse,
  } = useCollaboratorVariants(formData.collaborator_id || '', { limit: 200 });

  // Use variants from the collaborator-specific API endpoint
  // The API returns variants that belong to the selected collaborator
  // API: GET /api/v1/collaborators/{collaborator_id}/variants
  // Response structure: { success: true, data: ProductVariantResponse[], pagination: {...} }
  const collaboratorVariants = React.useMemo((): CollaboratorVariant[] => {
    if (!formData.collaborator_id || !collaboratorVariantsResponse) return [];
    
    // Extract variants from the API response
    // Possible shapes:
    // 1) ApiResponse: { success, data: [...], pagination?... }
    // 2) Nested: { data: { data: [...], pagination } }
    // 3) Direct array: [...]
    const responseData = collaboratorVariantsResponse as any;
    const variantsData =
      Array.isArray(responseData)
        ? responseData
        : Array.isArray(responseData?.data)
          ? responseData.data
          : Array.isArray(responseData?.data?.data)
            ? responseData.data.data
            : [];
    
    if (!Array.isArray(variantsData)) return [];
    
    return variantsData.map((variant: any): CollaboratorVariant => ({
      id: variant.id,
      product_name: variant.product?.name || variant.product_name || 'N/A',
      product_sku: variant.sku || variant.variant_sku || '',
      brand_name: variant.brand_name,
      variant_name: variant.variant_name || 'N/A',
    }));
  }, [formData.collaborator_id, collaboratorVariantsResponse]);

  const [items, setItems] = useState<CreatePurchaseOrderItem[]>([]);
  const [currentItem, setCurrentItem] = useState({
    variant_id: '',
    quantity: 0,
    unit_price: 0,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleItemChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setCurrentItem((prev) => ({
      ...prev,
      [name]: name === 'variant_id' ? value : parseFloat(value) || 0,
    }));
  };

  const addItem = () => {
    if (!currentItem.variant_id) {
      setError('Please select a variant');
      return;
    }
    if (currentItem.quantity <= 0) {
      setError('Quantity must be positive');
      return;
    }
    if (currentItem.unit_price < 0) {
      setError('Unit price cannot be negative');
      return;
    }

    setItems((prev) => [...prev, currentItem]);
    setCurrentItem({ variant_id: '', quantity: 0, unit_price: 0 });
    setError(null);
  };

  const removeItem = (index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const calculateTotal = () => {
    return items.reduce((sum, item) => sum + item.quantity * item.unit_price, 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setValidationErrors({});

    if (items.length === 0) {
      setError('Please add at least one item');
      return;
    }

    try {
      const poData: CreatePurchaseOrder = {
        ...formData,
        items,
      };

      const validated = createPurchaseOrderSchema.parse(poData);
      const result = await createPurchaseOrder(validated);

      dispatch(addPurchaseOrder(result as never));
      onSuccess?.();
    } catch (err) {
      if (err && typeof err === 'object' && 'errors' in err) {
        const zodError = err as { errors: Array<{ path: (string | number)[]; message: string }> };
        const errMap: Record<string, string> = {};
        zodError.errors.forEach((e) => {
          const field = String(e.path[0]);
          errMap[field] = e.message;
        });
        setValidationErrors(errMap);
      } else {
        setError(err instanceof Error ? err.message : 'An error occurred');
      }
    }
  };

  const getVariantDisplay = (variantId: string) => {
    const variant = collaboratorVariants.find((v: CollaboratorVariant) => v.id === variantId);
    if (!variant) return variantId;
    return `${variant.product_name} - ${variant.brand_name || variant.variant_name}`;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-error-50 border border-error-200 rounded-lg p-4">
          <p className="text-error-700">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label
            htmlFor="collaborator_id"
            className="block text-sm font-medium text-neutral-700 mb-2"
          >
            Supplier *
          </label>
          <select
            id="collaborator_id"
            name="collaborator_id"
            value={formData.collaborator_id}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            disabled={isLoading}
          >
            <option value="">Select supplier</option>
            {collaborators?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.company_name}
              </option>
            ))}
          </select>
          {validationErrors.collaborator_id && (
            <p className="text-error-600 text-sm mt-1">{validationErrors.collaborator_id}</p>
          )}
        </div>

        <div>
          <label htmlFor="warehouse_id" className="block text-sm font-medium text-neutral-700 mb-2">
            Warehouse *
          </label>
          <select
            id="warehouse_id"
            name="warehouse_id"
            value={formData.warehouse_id}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            disabled={isLoading}
          >
            <option value="">Select warehouse</option>
            {warehouses?.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>
          {validationErrors.warehouse_id && (
            <p className="text-error-600 text-sm mt-1">{validationErrors.warehouse_id}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label
            htmlFor="expected_delivery_date"
            className="block text-sm font-medium text-neutral-700 mb-2"
          >
            Expected Delivery Date *
          </label>
          <Input
            id="expected_delivery_date"
            name="expected_delivery_date"
            type="date"
            value={formData.expected_delivery_date}
            onChange={handleChange}
            disabled={isLoading}
          />
          {validationErrors.expected_delivery_date && (
            <p className="text-error-600 text-sm mt-1">{validationErrors.expected_delivery_date}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="is_inter_state"
            className="block text-sm font-medium text-neutral-700 mb-2"
          >
            GST Type
          </label>
          <select
            id="is_inter_state"
            name="is_inter_state"
            value={formData.is_inter_state === null ? '' : formData.is_inter_state ? 'true' : 'false'}
            onChange={(e) => {
              const value = e.target.value;
              setFormData((prev) => ({
                ...prev,
                is_inter_state: value === '' ? null : value === 'true',
              }));
            }}
            className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            disabled={isLoading}
          >
            <option value="">Auto-detect (based on addresses)</option>
            <option value="false">Intra-state (CGST + SGST)</option>
            <option value="true">Inter-state (IGST)</option>
          </select>
          <p className="text-neutral-600 text-xs mt-1">
            Leave as "Auto-detect" to let the system determine based on supplier and warehouse addresses
          </p>
        </div>
      </div>

      <div className="border-t border-neutral-200 pt-4">
        <h3 className="text-lg font-semibold text-neutral-900 mb-4">Items</h3>

        <div className="grid grid-cols-12 gap-2 mb-4">
          <div className="col-span-5">
            <label className="block text-sm font-medium text-neutral-700 mb-2">Variant *</label>
            <select
              name="variant_id"
              value={currentItem.variant_id}
              onChange={handleItemChange}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              disabled={isLoading || !formData.collaborator_id}
            >
              <option value="">
                {!formData.collaborator_id
                  ? 'Select supplier first'
                  : 'Select variant'}
              </option>
              {collaboratorVariants.map((variant: CollaboratorVariant) => (
                <option key={variant.id} value={variant.id}>
                  {variant.variant_name} {variant.product_sku ? `(SKU: ${variant.product_sku})` : ''}
                </option>
              ))}
            </select>
            {formData.collaborator_id &&
              collaboratorVariants.length === 0 && (
                <p className="text-xs text-neutral-600 mt-1">
                  No variants found for this supplier. Add variants with this collaborator in Product Inventory page first.
                </p>
              )}
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-medium text-neutral-700 mb-2">Quantity *</label>
            <Input
              name="quantity"
              type="number"
              min="1"
              step="1"
              value={currentItem.quantity || ''}
              onChange={handleItemChange}
              placeholder="0"
              disabled={isLoading}
              className="text-sm"
            />
          </div>

          <div className="col-span-3">
            <label className="block text-sm font-medium text-neutral-700 mb-2">Unit Price *</label>
            <Input
              name="unit_price"
              type="number"
              min="0"
              step="0.01"
              value={currentItem.unit_price || ''}
              onChange={handleItemChange}
              placeholder="0.00"
              disabled={isLoading}
              className="text-sm"
            />
          </div>

          <div className="col-span-2 flex items-end">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={addItem}
              disabled={isLoading}
              className="w-full"
            >
              <Icon name="Plus" size={16} className="mr-1" />
              Add
            </Button>
          </div>
        </div>

        {items.length > 0 && (
          <div className="bg-neutral-50 rounded-lg p-4 space-y-2">
            <div className="grid grid-cols-12 gap-2 text-sm font-medium text-neutral-600 pb-2 border-b border-neutral-200">
              <div className="col-span-5">Variant</div>
              <div className="col-span-2">Quantity</div>
              <div className="col-span-3">Unit Price</div>
              <div className="col-span-2">Total</div>
            </div>
            {items.map((item, index) => (
              <div key={index} className="grid grid-cols-12 gap-2 text-sm items-center">
                <div className="col-span-5 text-neutral-900">
                  {getVariantDisplay(item.variant_id)}
                </div>
                <div className="col-span-2 text-neutral-900">{item.quantity}</div>
                <div className="col-span-3 text-neutral-900">₹{item.unit_price.toFixed(2)}</div>
                <div className="col-span-1 text-neutral-900 font-medium">
                  ₹{(item.quantity * item.unit_price).toFixed(2)}
                </div>
                <div className="col-span-1 text-right">
                  <button
                    type="button"
                    onClick={() => removeItem(index)}
                    className="text-error-600 hover:text-error-700"
                    disabled={isLoading}
                  >
                    <Icon name="Trash2" size={16} />
                  </button>
                </div>
              </div>
            ))}
            <div className="border-t border-neutral-200 pt-2 mt-2">
              <div className="grid grid-cols-12 gap-2 text-sm font-semibold">
                <div className="col-span-10 text-right text-neutral-900">Total Amount:</div>
                <div className="col-span-2 text-neutral-900">₹{calculateTotal().toFixed(2)}</div>
              </div>
            </div>
          </div>
        )}

        {items.length === 0 && (
          <div className="text-center py-8 text-neutral-500 bg-neutral-50 rounded-lg">
            <Icon name="Package" size={32} className="mx-auto mb-2 text-neutral-400" />
            <p>No items added yet</p>
          </div>
        )}
      </div>

      <div className="flex gap-2 justify-end border-t border-neutral-200 pt-4">
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
        )}
        <Button type="submit" variant="primary" disabled={isLoading || items.length === 0}>
          {isLoading ? 'Creating...' : 'Create Purchase Order'}
        </Button>
      </div>
    </form>
  );
};
